import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse } from 'legacy/common/utils/error';
import {
  CreateOrganizationTitleDto,
  OrganizationTitleDto,
  OrganizationTitleResponseDto,
  OrganizationTitlesListResponseDto,
  ReorderOrganizationTitlesDto,
  UpdateOrganizationTitleDto
} from 'api/dtos/organizations/titles';
import {
  assignTitlesToMember,
  createOrganizationTitle,
  deleteOrganizationTitle,
  getOrganizationTitle,
  getOrganizationTitles,
  reorderOrganizationTitles,
  updateOrganizationTitle,
  removeTitleFromMember
} from 'api/services/organizations/titles';

export function useGetOrganizationTitles({
  organizationId,
  useUnauthenticatedEndpoint = false
}: {
  organizationId: string;
  useUnauthenticatedEndpoint?: boolean;
}) {
  return useQuery<OrganizationTitlesListResponseDto, ErrorResponse>(
    ['organizationTitles', { organizationId, useUnauthenticatedEndpoint }],
    () => getOrganizationTitles(organizationId, useUnauthenticatedEndpoint)
  );
}

export function useCreateOrganizationTitle() {
  return useMutation<
    OrganizationTitleDto,
    ErrorResponse,
    { organizationId: string; title: CreateOrganizationTitleDto }
  >(createOrganizationTitle);
}

export function useUpdateOrganizationTitle() {
  return useMutation<
    OrganizationTitleDto,
    ErrorResponse,
    {
      organizationId: string;
      titleId: string;
      title: UpdateOrganizationTitleDto;
    }
  >(updateOrganizationTitle);
}

export function useDeleteOrganizationTitle() {
  return useMutation<
    void,
    ErrorResponse,
    { organizationId: string; titleId: string }
  >(deleteOrganizationTitle);
}

export function useReorderOrganizationTitles() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ErrorResponse,
    { organizationId: string; data: ReorderOrganizationTitlesDto }
  >(reorderOrganizationTitles, {
    onMutate: async (variables) => {
      await queryClient.cancelQueries([
        'organizationTitles',
        { organizationId: variables.organizationId }
      ]);

      const previousTitles =
        queryClient.getQueryData<OrganizationTitlesListResponseDto>([
          'organizationTitles',
          { organizationId: variables.organizationId }
        ]);

      queryClient.setQueryData<OrganizationTitlesListResponseDto>(
        ['organizationTitles', { organizationId: variables.organizationId }],
        () => ({
          items: previousTitles?.items.map((title) => ({
            ...title,
            hierarchy:
              variables.data?.titles?.find((t) => t.id === title.id)
                ?.hierarchy || 0
          }))
        })
      );
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries([
        'organizationTitles',
        {
          organizationId
        }
      ]);
    }
  });
}

export function useGetOrganizationTitle({
  organizationId,
  titleId
}: {
  organizationId: string;
  titleId: string;
}) {
  return useQuery<OrganizationTitleResponseDto, ErrorResponse>(
    ['organizationTitle', { organizationId, titleId }],
    () => getOrganizationTitle({ organizationId, titleId })
  );
}

export function useAssignTitleToMember() {
  return useMutation<
    void,
    ErrorResponse,
    { organizationId: string; memberId: string; titles: string[] }
  >(assignTitlesToMember);
}

export function useRemoveTitleFromMember() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ErrorResponse,
    { organizationId: string; userId: string; titleId: string }
  >(removeTitleFromMember, {
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries([
        'organizationTitles',
        {
          organizationId
        }
      ]);
    }
  });
}
